import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { LocalKey } from "@base/constants/LocalKey";
import { apiAuthorize, apiUrl } from "@base/configs";
import { IAuth } from "@base/modules/auth/auth.interface";
import { clearSession } from "@base/modules/auth/session";
import { baseRoutes } from "@base/routes/baseRoutes";

declare module "axios" {
  export interface AxiosRequestConfig {
    /**
     * Opt out of the automatic logout that follows a failed refresh. For callers that are
     * already running their own teardown and would otherwise get a second, competing redirect.
     */
    skipAuthLogout?: boolean;
  }
}

const TOKEN_URL = `${apiUrl}/auth/oauth/token`;

class Services {
  axios: AxiosInstance;
  /** In-flight refresh, shared so concurrent requests trigger a single refresh call. */
  private refreshPromise: Promise<IAuth> | null = null;

  constructor() {
    this.axios = axios.create();
    this.axios.defaults.withCredentials = false;

    //! Interceptor request
    this.axios.interceptors.request.use(
      (config) => {
        const hasTurnstileToken = !!config.headers?.[`turn-stile-token`];
        const isTokenRequest = config.url === TOKEN_URL;

        const { accessToken } = this.getToken();

        if (accessToken && !isTokenRequest) {
          config.headers[`Authorization`] = `Bearer ${accessToken}`;
        } else if (!hasTurnstileToken) {
          config.headers[`Authorization`] = apiAuthorize;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    //! Interceptor response
    this.axios.interceptors.response.use(
      (config) => {
        return config;
      },
      async (error) => {
        const originalRequest = error.config;
        if (error.response) {
          if (error.response.status === 401 && !originalRequest._retry) {
            // The token endpoint has nothing to refresh, so a 401 there is final - a bad
            // credential on login/OTP, or a dead refresh token. Reject and let whoever asked
            // decide; that keeps the logout policy in exactly one place (the catch below).
            if (originalRequest.url === TOKEN_URL) {
              return Promise.reject(error);
            }

            // Access Token was expired
            originalRequest._retry = true;
            try {
              const auth = await this.refreshToken();

              if (auth.access_token) {
                originalRequest.headers[`Authorization`] = `Bearer ${auth.access_token}`;
              }

              return this.axios(originalRequest);
            } catch (_error) {
              // Session is unrecoverable - unless the caller is already tearing it down itself.
              if (!originalRequest.skipAuthLogout) {
                this.logout();
              }
            }
          }
        }

        return Promise.reject(error);
      },
    );
  }

  get<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.axios.get<T>(url, config);
  }

  post<T = any>(url: string, data: any, config?: AxiosRequestConfig) {
    return this.axios.post<T>(url, data, config);
  }

  delete<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.axios.delete<T>(url, config);
  }

  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.axios.put<T>(url, data, config);
  }

  patch<T = any>(url: string, data: any, config?: AxiosRequestConfig) {
    return this.axios.patch<T>(url, data, config);
  }

  setToken(auth: IAuth) {
    localStorage.setItem(LocalKey.AUTH, JSON.stringify(auth));
  }

  getToken() {
    const authLocal = localStorage.getItem(LocalKey.AUTH);

    if (authLocal) {
      const auth = JSON.parse(authLocal) as IAuth;

      return { accessToken: auth.access_token, refreshToken: auth.refresh_token };
    }

    return { accessToken: undefined, refreshToken: undefined };
  }

  refreshToken() {
    if (!this.refreshPromise) {
      this.refreshPromise = this.requestNewToken();
    }

    return this.refreshPromise;
  }

  private async requestNewToken(): Promise<IAuth> {
    try {
      const { refreshToken } = this.getToken();

      if (!refreshToken) {
        throw new Error("Missing refresh token");
      }

      const params = { grant_type: "refresh_token", refresh_token: refreshToken };

      const headers = {
        Authorization: apiAuthorize,
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
      };

      const response = await this.axios.post<IAuth>(TOKEN_URL, undefined, { headers, params });

      this.setToken(response.data);

      return response.data;
    } finally {
      this.refreshPromise = null;
    }
  }

  logout() {
    // The token is already dead here, so no point calling the logout API.
    clearSession(baseRoutes.login);
  }
}

export default new Services();
